/**
 * Project-scope contract — RO-2-08 conversion of the `scope` server action
 * (`excellent/src/scope-actions.ts`'s `setScopeCookie`, the whole of the
 * inventory's `scope` domain: 1 action, `docs/rollover/inventory/actions.md`).
 *
 *   POST /api/scope  → persist the "last project" hint as `excellent.scope`
 *
 * What this endpoint is NOT: it is not how a page learns its scope. Project-first
 * scope is derived from the URL (`resolveScopeFromPath`, `excellent/src/scope.ts`)
 * and the cookie is only a soft hint that seeds the switcher and a bare-`/`
 * restore. The switcher therefore calls this ALONGSIDE its `router.push`, and the
 * response carries no scope back — there is nothing to read that the caller did
 * not just send.
 *
 * `slug: null` means "All projects" and is stored as the literal `"all"` by the
 * handler, exactly as the source action did — the readers all special-case that
 * string, so the null↔"all" translation stays on the server side of the wire
 * rather than becoming a second thing every caller has to remember.
 */

import { z } from "zod";
import type { Operation } from "../types";

export const scopeSetBody = z.object({
  /** Project slug to make the active hint, or `null` for "All projects". */
  slug: z.string().min(1).nullable(),
});
export type ScopeSetBody = z.infer<typeof scopeSetBody>;

export const scopeSetResponse = z.object({ ok: z.literal(true) });
export type ScopeSetResponse = z.infer<typeof scopeSetResponse>;

export const scopeSet: Operation<void, void, ScopeSetBody, ScopeSetResponse> = {
  method: "POST",
  path: () => "/api/scope",
  body: scopeSetBody,
  response: scopeSetResponse,
};

export const scope = {
  set: scopeSet,
};
